import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { permissionListTag } from "permissions/_api/permissions-api-service";
import PermissionTemplateCommand from "permissions/_api/request-contracts/permission-template-command";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import PermissionTemplateSchema, {
  PermissionTemplateJSON,
} from "permissions/templates/_schemas/permission-template-schema";
import PermissionTemplate from "permissions/templates/permission-template";

type PermissionTemplateQueryArgs = {
  query: PermissionTemplateQuery;
};

type PermissionTemplateMutationArgs = {
  template: PermissionTemplate;
};

type PermissionTemplateByIdArgs = {
  id: string;
};

type PermissionTemplateDeleteArgs = {
  id: string;
  version: number;
};

export type PermissionTemplatesPage = Paginated<PermissionTemplateJSON> & {
  errorCount: number;
};

const jsonPatchHeaders = {
  "content-type": "application/json-patch+json",
};

const permissionTemplateListTag = { type: "PermissionTemplate" as const, id: "LIST" };
const getPermissionTemplateTags = (id?: string) =>
  id ? [permissionTemplateListTag, { type: "PermissionTemplate" as const, id }] : [permissionTemplateListTag];
/**
 * A rename or a delete changes what the grants that template made say about it: the flat read names
 * the template, and links it while it is live (#6828). So the cached grant lists are read again too.
 */
const getChangedTemplateTags = (id?: string) => [...getPermissionTemplateTags(id), permissionListTag];

export const permissionTemplatesApi = api.injectEndpoints({
  endpoints: (build) => ({
    addPermissionTemplate: build.mutation<PermissionTemplateJSON, PermissionTemplateMutationArgs>({
      query: ({ template }) => ({
        url: "permissions/templates",
        method: "POST",
        body: JSON.stringify(new PermissionTemplateCommand(template)),
        headers: jsonPatchHeaders,
      }),
      transformResponse: (response: unknown) => PermissionTemplateSchema.validateJSON(response),
      invalidatesTags: [permissionTemplateListTag],
    }),
    getPermissionTemplates: build.query<PermissionTemplatesPage, PermissionTemplateQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query.cacheKey,
      query: ({ query }) => ({
        url: "permissions/templates",
        params: query.searchParams,
      }),
      transformResponse: (response: unknown, meta): PermissionTemplatesPage => {
        const data: PermissionTemplateJSON[] = [];
        let errorCount = 0;

        requireArray(response, "permission templates response").forEach((datum) => {
          try {
            data.push(PermissionTemplateSchema.validateJSON(datum));
          } catch {
            errorCount++;
          }
        });

        return {
          data,
          headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
          errorCount,
        };
      },
      providesTags: [permissionTemplateListTag],
    }),
    getPermissionTemplateById: build.query<PermissionTemplateJSON, PermissionTemplateByIdArgs>({
      query: ({ id }) => `permissions/templates/${id}`,
      transformResponse: (response: unknown) => PermissionTemplateSchema.validateJSON(response),
      providesTags: (_result, _error, { id }) => [{ type: "PermissionTemplate" as const, id }],
    }),
    updatePermissionTemplate: build.mutation<PermissionTemplateJSON, PermissionTemplateMutationArgs>({
      query: ({ template }) => ({
        url: `permissions/templates/${template.id}`,
        method: "PUT",
        body: JSON.stringify(new PermissionTemplateCommand(template)),
        headers: jsonPatchHeaders,
      }),
      transformResponse: (response: unknown) => PermissionTemplateSchema.validateJSON(response),
      invalidatesTags: (_result, _error, { template }) => getChangedTemplateTags(template.id?.value),
    }),
    deletePermissionTemplateById: build.mutation<void, PermissionTemplateDeleteArgs>({
      query: ({ id, version }) => ({
        url: `permissions/templates/${id}`,
        method: "DELETE",
        params: { version },
      }),
      invalidatesTags: (_result, _error, { id }) => getChangedTemplateTags(id),
    }),
  }),
});

function requireArray(value: unknown, description: string): unknown[] {
  if (Array.isArray(value)) return value;
  throw new Error(`Expected ${description} to be an array.`);
}

export const {
  useAddPermissionTemplateMutation,
  useGetPermissionTemplatesQuery,
  useGetPermissionTemplateByIdQuery,
  useUpdatePermissionTemplateMutation,
  useDeletePermissionTemplateByIdMutation,
} = permissionTemplatesApi;
